import type { HospitalPatientCustomField } from './hospitalCustomFields';

/**
 * A patient's clinic-defined external id + custom-field values, as the
 * server returns them on `GET /patients/{id}` and the patients table.
 */
export type HospitalAttributes = {
  hospital_id?: number;
  patient_external_id?: string | null;
  patient_custom_fields?: Record<string, string | null | undefined>;
};

/**
 * The `hospital_attributes` block sent on `PATCH /patients/{id}`.
 *
 * Null on either value means "leave the stored one" — that is the server's
 * reading of it (`_apply_hospital_attribute_updates`), so the block only
 * ever carries what the clinician actually changed.
 */
export type HospitalAttributesPatch = {
  hospital_id: number;
  patient_external_id: string | null;
  patient_custom_fields: Record<string, string> | null;
};

/**
 * The custom-field values THIS clinic's schema owns, out of the merged bag
 * the server returns.
 *
 * This filter is the whole reason the module exists. The read merges
 * attributes across every hospital the caller can see (FOOD-1069 /
 * FOOD-1316), so the bag can hold keys belonging to another hospital. The
 * write targets ONE hospital's row and replaces `patient_custom_fields`
 * wholesale. Echoing the whole bag back would therefore copy the other
 * hospital's fields into this one — so only keys this clinic declares are
 * ever seeded or sent.
 */
export const customFieldsForClinic = (
  defs: HospitalPatientCustomField[],
  attributes: HospitalAttributes | null | undefined,
): Record<string, string> => {
  const bag = attributes?.patient_custom_fields ?? {};
  const out: Record<string, string> = {};
  for (const def of defs) {
    const value = bag[def.field];
    if (typeof value === 'string' && value !== '') out[def.field] = value;
  }
  return out;
};

/** Drop blanks and trim, so "  " and "" and absent all compare equal. */
const normalize = (fields: Record<string, string | undefined>): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) {
    const trimmed = (v ?? '').trim();
    if (trimmed) out[k] = trimmed;
  }
  return out;
};

const sameFields = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if (a[k] !== b[k]) return false;
  return true;
};

/**
 * The `hospital_attributes` block to PATCH, or null when nothing changed.
 *
 * Null on an untouched dialog matters: the write replaces both columns
 * wholesale and now needs rights the caller may not hold (FOOD-1071 makes a
 * refused write a 403), so an unchanged submit must not send the block at
 * all.
 *
 * `patient_external_id` is sent as null when the clinician blanked it,
 * because the server reads null as "leave the stored value". Clearing an
 * external id is therefore not possible from here — see the note in
 * `EditPatientDialog`.
 */
export const hospitalAttributesPatch = ({
  hospitalId,
  defs,
  hasExternalId,
  externalId,
  customFields,
  initial,
}: {
  hospitalId: number | undefined;
  /** This clinic's field definitions — the only keys that may be written. */
  defs: HospitalPatientCustomField[];
  /** Whether this clinic uses an external id at all. */
  hasExternalId: boolean;
  externalId: string;
  customFields: Record<string, string | undefined>;
  /** What the dialog was opened with. */
  initial: HospitalAttributes | null | undefined;
}): HospitalAttributesPatch | null => {
  if (hospitalId === undefined) return null;
  if (!hasExternalId && defs.length === 0) return null;

  const nextFields = normalize(
    Object.fromEntries(defs.map((d) => [d.field, customFields[d.field]])),
  );
  const prevFields = customFieldsForClinic(defs, initial);
  const fieldsChanged = !sameFields(prevFields, nextFields);

  const nextExternalId = hasExternalId ? externalId.trim() : '';
  const prevExternalId = (initial?.patient_external_id ?? '').trim();
  // A blank where there was a value is not a change we can express, so it
  // is not one we claim.
  const externalIdChanged =
    hasExternalId && nextExternalId !== '' && nextExternalId !== prevExternalId;

  if (!fieldsChanged && !externalIdChanged) return null;

  return {
    hospital_id: hospitalId,
    patient_external_id: externalIdChanged ? nextExternalId : null,
    // Null rather than `nextFields` when nothing moved: the column is
    // replaced wholesale, and a clinic that declares no fields would
    // otherwise write `{}` over values a previous schema had left behind.
    patient_custom_fields: fieldsChanged ? nextFields : null,
  };
};
